import React, { useState, useMemo } from 'react';

const MetaBalanceReductionList = ({ 
  records, 
  onEdit, 
  onDelete, 
  onShowHistory, 
  onQuickAction, 
  currentUser, 
  permissions 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('fecha_asunto');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const recordsPerPage = 10;

  // Opciones de estado
  const statusOptions = [
    { value: 'all', label: '📋 Todos', count: 0 },
    { value: 'pendiente', label: '⏳ Pendiente', count: 0, color: 'bg-yellow-100 text-yellow-800' },
    { value: 'en_proceso', label: '🔄 En Proceso', count: 0, color: 'bg-blue-100 text-blue-800' },
    { value: 'completado', label: '✅ Completado', count: 0, color: 'bg-green-100 text-green-800' },
    { value: 'cancelado', label: '❌ Cancelado', count: 0, color: 'bg-red-100 text-red-800' }
  ];

  // Calcular contadores por estado
  const statusCounts = useMemo(() => {
    const counts = {
      all: records.length,
      pendiente: 0,
      en_proceso: 0,
      completado: 0,
      cancelado: 0
    };

    records.forEach(record => {
      if (counts.hasOwnProperty(record.status)) {
        counts[record.status]++;
      }
    });

    return counts;
  }, [records]);

  // Filtrar y ordenar registros
  const filteredAndSortedRecords = useMemo(() => {
    let filtered = records.filter(record => {
      const matchesSearch = 
        record.nombre_cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.motivo_reduccion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.seguimiento?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.celula?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = activeTab === 'all' || record.status === activeTab;

      return matchesSearch && matchesStatus;
    });

    // Ordenar
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Manejar fechas
      if (sortBy === 'fecha_asunto' || sortBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Manejar números (saldos)
      if (sortBy === 'saldo_anterior' || sortBy === 'saldo_actual' || sortBy === 'diferencia_saldo') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      // Manejar strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [records, searchTerm, activeTab, sortBy, sortOrder]);

  // Paginación
  const totalPages = Math.ceil(filteredAndSortedRecords.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedRecords = filteredAndSortedRecords.slice(startIndex, startIndex + recordsPerPage);

  // Resetear página cuando cambian los filtros
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    const num = parseFloat(amount);
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendiente: { label: '⏳ Pendiente', class: 'bg-yellow-100 text-yellow-800' },
      en_proceso: { label: '🔄 En Proceso', class: 'bg-blue-100 text-blue-800' },
      completado: { label: '✅ Completado', class: 'bg-green-100 text-green-800' },
      cancelado: { label: '❌ Cancelado', class: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  const getReductionSeverity = (diferencia) => {
    const amount = parseFloat(diferencia) || 0;
    if (amount >= 1000) {
      return { class: 'text-red-600 font-bold', icon: '🔴' };
    } else if (amount >= 500) {
      return { class: 'text-orange-600 font-semibold', icon: '🟠' };
    } else if (amount >= 100) {
      return { class: 'text-yellow-600 font-medium', icon: '🟡' };
    } else {
      return { class: 'text-green-600', icon: '🟢' };
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↗️' : '↘️';
  };

  if (!records || records.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="text-6xl mb-4">📉</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          No hay reducciones de saldo registradas
        </h3>
        <p className="text-gray-500">
          Las reducciones de saldo aparecerán aquí una vez que sean creadas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pestañas de estado */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {statusOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setActiveTab(option.value)}
              className={`px-4 py-2 rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 ${
                activeTab === option.value
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span>{option.label}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                activeTab === option.value 
                  ? 'bg-white bg-opacity-20 text-white' 
                  : 'bg-gray-200 text-gray-700'
              }`}>
                {statusCounts[option.value] || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Controles de búsqueda y filtros */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar por cliente, motivo, agente o célula..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              />
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                🔍
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            >
              <option value="fecha_asunto">Ordenar por Fecha</option>
              <option value="nombre_cliente">Ordenar por Cliente</option>
              <option value="diferencia_saldo">Ordenar por Diferencia</option>
              <option value="saldo_anterior">Ordenar por Saldo Anterior</option>
              <option value="status">Ordenar por Estado</option>
              <option value="created_at">Ordenar por Creación</option>
            </select>
            
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors duration-200 font-medium"
            >
              {sortOrder === 'asc' ? '↗️ Ascendente' : '↘️ Descendente'}
            </button>
          </div>
        </div>

        {/* Información de resultados */}
        <div className="flex justify-between items-center mb-4">
          <p className="text-gray-600">
            Mostrando {paginatedRecords.length} de {filteredAndSortedRecords.length} reducciones
            {activeTab !== 'all' && ` (${statusOptions.find(opt => opt.value === activeTab)?.label})`}
          </p>
          
          {filteredAndSortedRecords.length > recordsPerPage && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
              >
                ← Anterior
              </button>
              
              <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-medium">
                {currentPage} de {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Lista de registros */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {paginatedRecords.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-4">🔍</div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No se encontraron reducciones
            </h3>
            <p className="text-gray-500">
              Intenta ajustar los filtros de búsqueda o crear una nueva reducción.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-green-50 to-emerald-50">
                <tr>
                  <th 
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-green-100 transition-colors duration-200"
                    onClick={() => handleSort('fecha_asunto')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Fecha</span>
                      <span>{getSortIcon('fecha_asunto')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-green-100 transition-colors duration-200"
                    onClick={() => handleSort('nombre_cliente')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Cliente</span>
                      <span>{getSortIcon('nombre_cliente')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-green-100 transition-colors duration-200"
                    onClick={() => handleSort('saldo_anterior')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Saldo Anterior</span>
                      <span>{getSortIcon('saldo_anterior')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-green-100 transition-colors duration-200"
                    onClick={() => handleSort('saldo_actual')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Saldo Actual</span>
                      <span>{getSortIcon('saldo_actual')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-green-100 transition-colors duration-200"
                    onClick={() => handleSort('diferencia_saldo')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Diferencia</span>
                      <span>{getSortIcon('diferencia_saldo')}</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Motivo
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-green-100 transition-colors duration-200"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Estado</span>
                      <span>{getSortIcon('status')}</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRecords.map((record, index) => {
                  const severity = getReductionSeverity(record.diferencia_saldo);
                  
                  return (
                    <tr 
                      key={record.id} 
                      className={`hover:bg-gray-50 transition-colors duration-200 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatDate(record.fecha_asunto)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {record.mes}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {record.nombre_cliente}
                        </div>
                        <div className="text-xs text-gray-500">
                          {record.celula}
                        </div>
                        {record.cuenta_publicitaria && (
                          <a 
                            href={record.cuenta_publicitaria}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-green-600 hover:text-green-800 underline"
                          >
                            Ver cuenta
                          </a>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(record.saldo_anterior)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(record.saldo_actual)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium flex items-center ${severity.class}`}>
                          <span className="mr-1">{severity.icon}</span>
                          {formatCurrency(record.diferencia_saldo)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={record.motivo_reduccion}>
                          {record.motivo_reduccion}
                        </div>
                        <div className="text-xs text-gray-500">
                          {record.seguimiento}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(record.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onQuickAction(record)}
                            className="text-yellow-600 hover:text-yellow-900 transition-colors duration-200 p-2 hover:bg-yellow-50 rounded-lg"
                            title="Acción rápida"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M13 3L4 14h6v7l9-11h-6V3z"/>
                            </svg>
                          </button>
                          
                          <button
                            onClick={() => onShowHistory(record)}
                            className="text-gray-600 hover:text-gray-900 transition-colors duration-200 p-2 hover:bg-gray-50 rounded-lg"
                            title="Ver historial"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                            </svg>
                          </button>
                          
                          <button
                            onClick={() => onEdit(record)}
                            className="text-orange-600 hover:text-orange-900 transition-colors duration-200 p-2 hover:bg-orange-50 rounded-lg"
                            title="Editar"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                          </button>
                          
                          {(permissions?.canDelete || currentUser?.role === 'admin') && (
                            <button
                              onClick={() => onDelete(record.id)}
                              className="text-gray-600 hover:text-gray-900 transition-colors duration-200 p-2 hover:bg-gray-50 rounded-lg"
                              title="Eliminar"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginación inferior */}
      {filteredAndSortedRecords.length > recordsPerPage && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex justify-between items-center">
            <p className="text-gray-600">
              Página {currentPage} de {totalPages} ({filteredAndSortedRecords.length} reducciones total)
            </p>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
              >
                ⏮️ Primera
              </button>
              
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
              >
                ← Anterior
              </button>
              
              <span className="px-4 py-2 bg-green-100 text-green-800 rounded-lg font-medium">
                {currentPage}
              </span>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
              >
                Siguiente →
              </button>
              
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
              >
                ⏭️ Última
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaBalanceReductionList;
